import { VerticalContainer } from '@american-autonomy/ag-ui';

import StepperField from 'components/StepperField';

import HomePointControl from 'pages/ApplicationDetailsPage/components/FlightPlannerPanel/components/CoverageOptionsSection/components/HomePointControl';
import WindDirectionControl from 'pages/ApplicationDetailsPage/components/FlightPlannerPanel/components/CoverageOptionsSection/components/WindDirectionControl';
import {
    CORNER_FILL_OFF_INDEX,
    CORNER_FILL_ON_INDEX,
    CORNER_FILL_ON_VALUE,
    CORNER_FILL_OPTIONS,
    COVERAGE_AREA_OPTIONS,
    HEADLAND_ORDER_OPTIONS,
    HEADLAND_PASSES,
    LABELS
} from 'pages/ApplicationDetailsPage/components/FlightPlannerPanel/components/CoverageOptionsSection/constants';
import LabeledToggle from 'pages/ApplicationDetailsPage/components/FlightPlannerPanel/components/LabeledToggle';
import { VARIANT_SWITCH } from 'pages/ApplicationDetailsPage/components/FlightPlannerPanel/components/LabeledToggle/constants';
import useApplicationDetailsTranslation from 'pages/ApplicationDetailsPage/hooks/useTranslation';

import { TEST_ID } from './constants';
import type {
    CoverageArea,
    FunctionComponent,
    HeadlandOrder,
    Props,
    RoutePlanConfig
} from './types';

import styles from './styles.module.scss';

const CoverageOptionsBody: FunctionComponent<Props> = ({
    className = '',
    config,
    isHomePointPickDisabled,
    isPickingHomePoint,
    onConfigChange,
    onPickHomePoint,
    testId = TEST_ID
}) => {
    const { CORNER_FILL_HINT } = useApplicationDetailsTranslation();
    const headlandOrderIndex = HEADLAND_ORDER_OPTIONS.findIndex(
        option => option.value === config.headlandOrder
    );
    const coverageAreaIndex = COVERAGE_AREA_OPTIONS.findIndex(
        option => option.value === config.coverageArea
    );
    const cornerFillIndex = config.cornerFill ? CORNER_FILL_ON_INDEX : CORNER_FILL_OFF_INDEX;
    const update = (partial: Partial<RoutePlanConfig>) => onConfigChange({ config: partial });

    return (
        <VerticalContainer className={`${className} ${styles.coverageOptionsBody}`} testId={testId}>
            <StepperField
                label={LABELS.headlandPasses}
                max={HEADLAND_PASSES.max}
                min={HEADLAND_PASSES.min}
                onChange={({ value }) => update({ headlandPasses: value })}
                step={HEADLAND_PASSES.step}
                value={config.headlandPasses}
            />

            <LabeledToggle
                activeIndex={headlandOrderIndex}
                label={LABELS.headlandOrder}
                onClick={value => update({ headlandOrder: value as HeadlandOrder })}
                options={HEADLAND_ORDER_OPTIONS}
            />

            <LabeledToggle
                activeIndex={coverageAreaIndex}
                label={LABELS.coverageArea}
                onClick={value => update({ coverageArea: value as CoverageArea })}
                options={COVERAGE_AREA_OPTIONS}
            />

            <LabeledToggle
                activeIndex={cornerFillIndex}
                hint={CORNER_FILL_HINT}
                label={LABELS.cornerFill}
                onClick={value => update({ cornerFill: value === CORNER_FILL_ON_VALUE })}
                options={CORNER_FILL_OPTIONS}
                variant={VARIANT_SWITCH}
            />

            <WindDirectionControl
                onChange={({ windFromDegrees }) => update({ windFromDegrees })}
                value={config.windFromDegrees}
            />

            <HomePointControl
                homePoint={config.homePoint}
                isDisabled={isHomePointPickDisabled}
                isPicking={isPickingHomePoint}
                onPickHomePoint={onPickHomePoint}
            />
        </VerticalContainer>
    );
};

export default CoverageOptionsBody;
